import { useMemo } from 'react';
import { useApi } from './useApi';
import type { Match, MatchEvent, MatchType, MatchFormat, Substitution, UpdateMatchInput } from '../types';

export interface CreateMatchInput {
  date: string;
  matchLengthMin: number;
  matchType?: MatchType;
  matchFormat?: MatchFormat;
  matchTitle?: string;
  kickoff?: string;
  opposition?: string;
  homeAway?: 'home' | 'away';
  ourScore?: number;
  theirScore?: number;
  /** @deprecated use positions[] */
  position?: string;
  positions?: string[];
  kidRating?: number;
  manOfMatch?: boolean;
  captain?: boolean;
  substitutions?: Substitution[];
  note?: string;
  events?: MatchEvent[];
  minutesPlayed?: number;
}

export interface CreateMatchResponse {
  match: Match;
  newAchievements: Array<{
    id: string;
    title: string;
    description: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
  }>;
}

export function useMatchApi() {
  const call = useApi();
  return useMemo(() => ({
    list: (kidId: string) =>
      call<Match[]>(`/api/v1/kids/${encodeURIComponent(kidId)}/matches`),
    get: (kidId: string, matchId: string) =>
      call<Match>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}`
      ),
    create: (kidId: string, input: CreateMatchInput) =>
      call<CreateMatchResponse>(`/api/v1/kids/${encodeURIComponent(kidId)}/matches`, {
        method: 'POST',
        body: input,
      }),
    // PATCH now returns the same envelope as POST so the two-stage match
    // flow (MatchAdd setup → MatchRecord events) can fire the Full-Time
    // reveal with the achievement diff just like POST does. MatchEdit
    // doesn't read the response, so the contract change is transparent.
    update: (kidId: string, matchId: string, patch: UpdateMatchInput) =>
      call<CreateMatchResponse>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}`,
        { method: 'PATCH', body: patch }
      ),
    remove: (kidId: string, matchId: string) =>
      call<{ ok: true }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}`,
        { method: 'DELETE' }
      ),
    presignPhoto: (kidId: string, matchId: string, contentType: string, size: number) =>
      call<{ uploadUrl: string; fields: Record<string, string>; key: string; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photo/presign`,
        { method: 'POST', body: { contentType, size } }
      ),
    /**
     * Tell the server an S3 upload completed and the new key should be added
     * to the match's photo set. The server enforces the per-plan cap.
     */
    confirmPhoto: (kidId: string, matchId: string, key: string) =>
      call<{ ok: true; imagePaths: string[] }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photo/confirm`,
        { method: 'POST', body: { key } }
      ),
    /** Voice note: presigned POST for the recording (Stats/Reports). */
    presignVoice: (kidId: string, matchId: string, contentType: string, size: number, durationSec: number) =>
      call<{ uploadUrl: string; fields: Record<string, string>; key: string; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/voice/presign`,
        { method: 'POST', body: { contentType, size, durationSec } }
      ),
    /** Voice note: upload done → start the Transcribe job; match goes 'pending'. */
    confirmVoice: (kidId: string, matchId: string, key: string, durationSec: number) =>
      call<{ ok: true; status: 'pending'; jobName: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/voice/confirm`,
        { method: 'POST', body: { key, durationSec } }
      ),
    /** Voice note: delete audio + transcript. */
    deleteVoice: (kidId: string, matchId: string) =>
      call<{ ok: true }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/voice`,
        { method: 'DELETE' }
      ),
    /** Primary photo URL — used by dashboard top-story and other single-image cards. */
    photoUrl: (kidId: string, matchId: string) =>
      call<{ url: string; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photo-url`
      ),
    /** Full gallery — signed URLs for every photo on this match. */
    listPhotos: (kidId: string, matchId: string) =>
      call<{ photos: Array<{ key: string; suffix: string; url: string }>; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photos`
      ),
    /** Remove every photo on the match (legacy / nuke). */
    removePhoto: (kidId: string, matchId: string) =>
      call<{ ok: true }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photo`,
        { method: 'DELETE' }
      ),
    /** Remove a single photo by its trailing key suffix. */
    removePhotoBySuffix: (kidId: string, matchId: string, suffix: string) =>
      call<{ ok: true; imagePaths: string[] }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/photo/${encodeURIComponent(suffix)}`,
        { method: 'DELETE' }
      ),
    /**
     * Create a public share token for this match. Returns `shareUrl` — a
     * server-side URL whose response contains OG meta tags so social
     * platforms render a preview card.
     */
    createShare: (kidId: string, matchId: string) =>
      call<{ token: string; shareUrl: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/share`,
        { method: 'POST', body: {} }
      ),
    /**
     * Generate (or regenerate) the AI match report for this match.
     * Reports tier only — the server returns 403 plan_required for
     * other plans.
     */
    generateReport: (kidId: string, matchId: string) =>
      call<{ report: { text: string; generatedAt: string; modelId: string } }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/report`,
        { method: 'POST', body: {} }
      ),
    /**
     * Horizon 1 / H1.2 — "Send to family" recap email. Server-rendered
     * (no Bedrock). Rate limit: 5 sends per match per UTC day per parent.
     */
    sendToFamily: (kidId: string, matchId: string, recipientEmail: string) =>
      call<{ ok: true; sentTo: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/matches/${encodeURIComponent(matchId)}/send`,
        { method: 'POST', body: { recipientEmail } }
      ),
  }), [call]);
}
